import type { Screen } from '../shell.tsx';

/**
 * A readiness gap → what to do about it and where.
 *
 * The API returns readiness gaps as finished sentences ("no assembly point is
 * set") in a deliberate criticality order (`api/src/routes/places.ts`); this
 * module restates each as an action somebody takes ("Set an assembly point"),
 * names the screen that fixes it, and assigns a rank so a gap can be ordered by
 * urgency. Matched in order, first hit wins; anything unrecognised falls through
 * as itself so a new gap the API grows still surfaces rather than vanishing.
 *
 * It used to live inside `screens/home.tsx`, where the Home to-do slab consumed
 * it. That slab is gone (`design-dashboard.md` §8 — only what cannot wait floats
 * on the map). The mapping still earns its place: the readiness screen turns
 * each gap into a one-tap row that routes to where it is fixed, and its contract
 * test drives every gap string the API emits through `classifyGap`. Keeping it
 * in its own module lets both the readiness screen and that test import it
 * without the home ⇄ readiness import cycle that living in `home.tsx` created.
 */
const GAP_MATCHERS: { re: RegExp; rank: number; screen: Screen; label: (n: string) => string }[] = [
  // Whether a real alert can be sent here at all (migration 0134) — ahead of
  // everything, because nothing else on this list matters if it cannot.
  {
    re: /needs two administrators for a real alert/,
    rank: 1,
    screen: 'people',
    label: () => 'Add a second administrator — no real alert can be sent here without one',
  },
  {
    re: /needs two administrators but has no linked teams/,
    rank: 8,
    screen: 'teams',
    label: () => "Link this building's teams so only its people need two administrators",
  },
  {
    // The authorise sheet's Call (migration 0138): an administrator nobody can
    // ring before a request for a real alert lapses.
    re: /no work mobile/,
    rank: 9,
    screen: 'people',
    label: (n) =>
      `Add a work mobile for ${n} ${n === '1' ? 'administrator' : 'administrators'} — whoever authorises their alerts can't call them`,
  },
  {
    re: /nobody has chosen who can raise a real alert/,
    rank: 9,
    screen: 'locations',
    label: () => 'Choose who can raise a real alert here',
  },
  // Life-safety placement — a building cannot run an evacuation without these.
  {
    re: /no assembly point is set/,
    rank: 10,
    screen: 'locations',
    label: () => 'Set an assembly point',
  },
  {
    re: /no indoor shelter location/,
    rank: 11,
    screen: 'locations',
    label: () => 'Set an indoor shelter location',
  },
  { re: /no areas are defined/, rank: 12, screen: 'locations', label: () => 'Add areas to sweep' },
  {
    // The ECO / "who sweeps where" board lives on Locations with the building it
    // is about (locked IA 2026-09-07), so warden gaps route there, not to People.
    re: /no Chief Warden is appointed/i,
    rank: 13,
    screen: 'locations',
    label: () => 'Appoint a Chief Warden',
  },
  {
    re: /no warden assigned to sweep/,
    rank: 14,
    screen: 'locations',
    label: (n) => `Assign wardens to ${n} ${n === '1' ? 'area' : 'areas'}`,
  },
  {
    // An SOS at this building pages its responders (migration 0171), and the
    // switch is on with nobody reachable — the one state worse than off. The
    // team and the switch live on the building's page.
    re: /SOS from people at this building reaches nobody/,
    rank: 14,
    screen: 'locations',
    label: () => 'SOS here reaches nobody — name a response team whose members have the app',
  },
  {
    // The paid plan's text backup for wardens without the app (migration
    // 0147) is not switched on for this deployment — said rather than found
    // out mid-sweep. Nothing on Locations fixes it; Settings is where the plan
    // and the organisation's contact with us live.
    re: /text backup for wardens/,
    rank: 15,
    screen: 'settings',
    label: () => "Wardens can only answer in the app — the text backup isn't set up yet",
  },
  {
    re: /no map location/,
    rank: 18,
    screen: 'locations',
    label: () => 'Place this building on the map',
  },
  // Who would actually be reached, and whether the building has ever rehearsed.
  {
    re: /no way of being reached/,
    rank: 20,
    screen: 'people',
    label: (n) => `Add contact details for ${n} ${n === '1' ? 'person' : 'people'}`,
  },
  {
    re: /on the roster, so/,
    rank: 21,
    screen: 'people',
    label: () => 'Add the rest of the roster',
  },
  {
    // Members the app cannot reach (docs/app-adoption.md, migration 0176):
    // email or text still carries the alert, so this ranks below the
    // unreachable, and the nudge lives on People.
    re: /have the EVAC app/,
    rank: 24,
    screen: 'people',
    label: (n) => `Ask ${n} ${n === '1' ? 'person' : 'people'} to install the EVAC app`,
  },
  {
    re: /no drill has ever been completed/,
    rank: 22,
    screen: 'activate',
    label: () => 'Run the first drill',
  },
  {
    re: /last drill was more than twelve months/,
    rank: 23,
    screen: 'activate',
    label: () => 'Run a drill — the last was over a year ago',
  },
  // Accessibility — a route somebody cannot take is not a route.
  {
    re: /reachable without stairs/,
    rank: 30,
    screen: 'locations',
    label: () => 'Add a step-free assembly point',
  },
  {
    re: /step-free route that has not been written/,
    rank: 31,
    screen: 'locations',
    label: (n) => `Write a step-free route for ${n} ${n === '1' ? 'person' : 'people'}`,
  },
  {
    re: /nobody assigned to assist/,
    rank: 32,
    screen: 'locations',
    label: (n) => `Assign help for ${n} evacuation ${n === '1' ? 'plan' : 'plans'}`,
  },
  {
    re: /no step-free approach instruction/,
    rank: 33,
    screen: 'locations',
    label: (n) => `Write step-free directions for ${n} assembly ${n === '1' ? 'point' : 'points'}`,
  },
  {
    re: /never been checked for step-free/,
    rank: 34,
    screen: 'locations',
    label: (n) => `Check ${n} assembly ${n === '1' ? 'point' : 'points'} for step-free access`,
  },
  // Diagrams and equipment — recorded, current, in date.
  {
    re: /no evacuation diagrams are recorded/,
    rank: 40,
    screen: 'locations',
    label: () => 'Add the evacuation diagrams',
  },
  {
    re: /past review/,
    rank: 41,
    screen: 'locations',
    label: (n) => `Re-check ${n} evacuation ${n === '1' ? 'diagram' : 'diagrams'}`,
  },
  {
    re: /no emergency equipment is recorded/,
    rank: 42,
    screen: 'locations',
    label: () => 'Record the emergency equipment',
  },
  {
    re: /(has|have) expired/,
    rank: 43,
    screen: 'locations',
    label: (n) => `Replace ${n} expired ${n === '1' ? 'item' : 'items'}`,
  },
  {
    re: /overdue for a service check/,
    rank: 44,
    screen: 'locations',
    label: (n) => `Service ${n} ${n === '1' ? 'item' : 'items'}`,
  },
  // Upkeep — worth doing, never life-or-death, so last.
  {
    re: /marked as the default/,
    rank: 50,
    screen: 'locations',
    label: () => 'Mark a default assembly point',
  },
  {
    re: /would need placing/,
    rank: 51,
    screen: 'locations',
    label: () => 'Add assembly-point capacity',
  },
  {
    re: /overdue for review/,
    rank: 52,
    screen: 'locations',
    label: (n) => `Review ${n} evacuation ${n === '1' ? 'plan' : 'plans'}`,
  },
  {
    re: /lapse.* within a fortnight/,
    rank: 53,
    screen: 'locations',
    label: (n) => `Renew ${n} evacuation ${n === '1' ? 'plan' : 'plans'}`,
  },
  {
    re: /due for review within six months/,
    rank: 54,
    screen: 'locations',
    label: (n) => `Review ${n} ${n === '1' ? 'diagram' : 'diagrams'} due soon`,
  },
  {
    re: /within a fortnight/,
    rank: 55,
    screen: 'locations',
    label: (n) => `Renew ${n} ${n === '1' ? 'item' : 'items'} expiring soon`,
  },
];

/** First-capitalise, mirroring `ui.sentence`, for a gap no matcher recognised. */
function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * The fallthrough rank for a gap no matcher recognises — worse than every real
 * one, so an unclassified gap sinks rather than jumping the queue. Named so the
 * contract test can assert no real API gap ever lands here.
 */
export const GAP_FALLTHROUGH_RANK = 90;

/**
 * One readiness gap → what to do about it, where, and how urgent, or the
 * fallthrough when no matcher recognises it. Exported so `home-todo-contract`
 * can drive every gap string `api/src/routes/places.ts` actually emits through
 * it and fail CI if a reword drops a life-safety row to the fallthrough or the
 * wrong screen — the reverse-engineering the CLAUDE.md anti-pattern warns about,
 * pinned to the real source rather than to hand-typed fixtures.
 */
export function classifyGap(gap: string): {
  matched: boolean;
  label: string;
  screen: Screen;
  rank: number;
} {
  const n = gap.match(/\d+/)?.[0] ?? '';
  const m = GAP_MATCHERS.find((x) => x.re.test(gap));
  if (!m)
    return {
      matched: false,
      label: capitalise(gap),
      screen: 'locations',
      rank: GAP_FALLTHROUGH_RANK,
    };
  return { matched: true, label: m.label(n), screen: m.screen, rank: m.rank };
}
